/**
 * MediaImage
 *
 * Drop-in replacement for a bare <img> for user-uploaded media. It smooths the
 * first-load "blip" testers reported by:
 *   - reserving correctly-proportioned space before the bytes arrive, via the
 *     intrinsic width/height stored at upload time. When those attributes are
 *     present the browser derives an `aspect-ratio` and uses them as the
 *     pre-load natural size, so the box is sized before the image loads even
 *     though the surrounding CSS keeps `width/height: auto` + max-size caps.
 *     This is the standard fix for image layout shift (CLS).
 *   - fading the image in over a neutral placeholder tint once it loads.
 *
 * It renders a single <img> (no wrapper element), so all existing context CSS
 * that targets `.question > img`, `.option img`, `.assessment-image-header img`,
 * etc. keeps applying unchanged. Fade/placeholder styling is scoped to the
 * `.media-image` class (see App.css). When dimensions are unknown (SVGs, or
 * images uploaded before dimensions were tracked) it simply fades in with no
 * space reservation — the same visual as before, minus the flash.
 */

import React, { useState } from 'react';

interface MediaImageProps {
  src: string;
  alt: string;
  /** Intrinsic pixel width, if known (null/undefined for SVG or legacy uploads). */
  width?: number | null;
  /** Intrinsic pixel height, if known. */
  height?: number | null;
  /** Optional extra class applied to the <img> (e.g. existing "option-image-thumb"). */
  className?: string;
}

export const MediaImage: React.FC<MediaImageProps> = ({
  src,
  alt,
  width,
  height,
  className,
}) => {
  const [loaded, setLoaded] = useState(false);

  const hasDimensions =
    typeof width === 'number' && typeof height === 'number' && width > 0 && height > 0;

  const classes = ['media-image'];
  if (className) classes.push(className);
  if (hasDimensions) classes.push('has-dimensions');
  if (loaded) classes.push('is-loaded');

  return (
    <img
      src={src}
      alt={alt}
      className={classes.join(' ')}
      // Providing intrinsic dimensions lets the browser reserve aspect-correct
      // space before load; omitted when unknown so nothing is mis-sized.
      width={hasDimensions ? width! : undefined}
      height={hasDimensions ? height! : undefined}
      onLoad={() => setLoaded(true)}
      // On error, still reveal so the browser's broken-image indicator shows
      // instead of an invisible box stuck at opacity 0.
      onError={() => setLoaded(true)}
      // Cached images may already be complete before onLoad is wired up; reflect
      // that on mount so they don't stay stuck at opacity 0.
      ref={(node) => {
        if (node?.complete && !loaded) {
          setLoaded(true);
        }
      }}
    />
  );
};
